import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { QuestionService } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormWrapperConfig,
  UnwrapSignal,
} from '@tellent-ask-hr/dynamic-forms-types';
import type { NumberTemplateItem } from '@tellent-ask-hr/templates-types';

export class NumberConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionService = inject(QuestionService);
  private readonly i18n = inject(I18nService);

  public readonly kind: NumberTemplateItem['type'] = 'number';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.number'),
    icon: 'numeric',
  };

  public readonly components = {
    define: () => import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.QuestionComponent),
    respond: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.RespondTextComponent),
    present: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.TextPresenterComponent),
  };

  public readonly defineForm = (
    data?: DefineFormData,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').QuestionComponent['defineForm']
  > => this.questionService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondFormWrapperConfig,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondTextComponent['respondConfig']
  > => ({
    ...data,
    kind: 'number',
    placeholder: this.i18n.translate('tlc.ask_hr.form_template_create.configs.respond_placeholder'),
  });

  public readonly respondForm = (config: {
    required: boolean;
  }): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondTextComponent['respondForm']
  > =>
    this.formBuilder.group({
      value: this.formBuilder.control<string | null>(
        null,
        config.required ? Validators.required : [],
      ),
    });

  public readonly presentConfig = (
    data: NumberTemplateItem,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').TextPresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value ? `${data.value}` : null,
    kind: {
      type: 'text',
      multiline: false,
    },
  });
}
